import { useState } from "react";
import { motion } from "framer-motion";
import { Star, RotateCcw, ArrowRight, Trophy, Loader2 } from "lucide-react";
import TriviaGame from "./TriviaGame";
import MemoramaGame from "./MemoramaGame";
import EscapeRoom from "./EscapeRoom";

const ease = [0.16, 1, 0.3, 1];

function Stars({ n }) {
  return (
    <div className="flex items-center justify-center gap-1.5" data-testid="game-stars">
      {[0, 1, 2].map((i) => (
        <Star key={i} className={`w-8 h-8 ${i < n ? "fill-gold text-gold" : "text-navy/15"}`} />
      ))}
    </div>
  );
}

// Envoltorio reutilizable para jugar cualquier tipo y mostrar el resultado.
// props: type, config, onSubmit(payload)->result, onCheck(stage,answer)->bool (escape),
//        onContinue (opcional, para cursos), continueLabel
export default function GamePlay({ type, config, onSubmit, onCheck, onContinue, continueLabel = "Continuar" }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [round, setRound] = useState(0); // fuerza re-montaje del juego al reintentar

  const submit = async (payload) => {
    setBusy(true);
    try { const r = await onSubmit(payload); setResult(r); }
    finally { setBusy(false); }
  };

  const retry = () => { setResult(null); setRound((r) => r + 1); };

  if (result) {
    const passed = result.passed && result.score >= 40;
    return (
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, ease }}
        className="text-center py-6" data-testid="game-result">
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${passed ? "bg-emerald-500/10 text-emerald-500" : "bg-gold/15 text-gold"}`}>
          <Trophy className="w-8 h-8" />
        </div>
        <Stars n={result.stars} />
        <h3 className="text-4xl font-editorial mt-4" data-testid="game-score">{result.score}%</h3>
        <p className="text-navy/55 mt-1 text-sm">
          {type === "trivia" && `${result.detail?.correct ?? 0} de ${result.detail?.total ?? 0} correctas`}
          {type === "escape" && `${result.detail?.solved ?? 0} de ${result.detail?.total ?? 0} retos resueltos`}
          {type === "memorama" && `Completado en ${result.detail?.moves ?? 0} movimientos`}
        </p>
        <div className="flex items-center justify-center gap-3 mt-6">
          <button data-testid="game-retry" onClick={retry} className="rounded-full border border-navy/15 text-sm px-5 py-3 hover:bg-navy/5 inline-flex items-center gap-2">
            <RotateCcw className="w-4 h-4" /> Reintentar
          </button>
          {onContinue && (
            <button data-testid="game-continue" onClick={() => onContinue(result)} className="rounded-full bg-gradient-violet text-white text-sm px-6 py-3 hover:opacity-90 inline-flex items-center gap-2">
              {continueLabel} <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </motion.div>
    );
  }

  return (
    <div key={round}>
      {type === "trivia" && <TriviaGame config={config} busy={busy} onComplete={(answers) => submit({ answers })} />}
      {type === "memorama" && <MemoramaGame config={config} busy={busy} onComplete={({ moves, seconds }) => submit({ moves, seconds })} />}
      {type === "escape" && <EscapeRoom config={config} busy={busy} onCheck={onCheck} onComplete={(text_answers) => submit({ text_answers })} />}
      {busy && <div className="mt-3 text-xs text-navy/40 inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Calculando…</div>}
    </div>
  );
}
